import { getSuperAdminOverview, getSuperAdminRevenueTrend, getSuperAdminCenterDetail, getSuperAdminCenterTrend, getSuperAdminRefundAnalysis, getSuperAdminReRegistrationSuccessMembers } from '../api/superAdminDashboard';
import { getItemWithExpiration } from '../lib/utils';

export const fetchSuperAdminOverview = (superAdminId: string, period: string = 'monthly', date?: string, week?: number, day?: number, includeGroup?: boolean) => ({
    queryKey: ['superAdminOverview', superAdminId, period, date || '', week ?? '', day ?? '', includeGroup ?? false],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        return await getSuperAdminOverview(superAdminId, period, authorization, date, week, day, includeGroup);
    },
});

export const fetchSuperAdminRevenueTrend = (superAdminId: string, period: string, date?: string, includeGroup?: boolean) => ({
    queryKey: ['superAdminRevenueTrend', superAdminId, period, date || '', includeGroup ?? false],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        return await getSuperAdminRevenueTrend(superAdminId, period, authorization, date, includeGroup);
    },
});

export const fetchSuperAdminRefundAnalysis = (
    superAdminId: string,
    period: string = 'monthly',
    date?: string,
    week?: number,
    day?: number,
    includeGroup?: boolean,
    includeFacility?: boolean,
) => ({
    queryKey: ['superAdminRefundAnalysis', superAdminId, period, date || '', week ?? '', day ?? '', includeGroup ?? false, includeFacility ?? false],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        return await getSuperAdminRefundAnalysis(superAdminId, period, authorization, date, week, day, includeGroup, includeFacility);
    },
});

export const fetchSuperAdminCenterTrend = (superAdminId: string, centerId: string, period: string, metric: string = 'revenue', date?: string, includeGroup?: boolean) => ({
    queryKey: ['superAdminCenterTrend', superAdminId, centerId, period, metric, date || '', includeGroup ?? false],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        return await getSuperAdminCenterTrend(superAdminId, centerId, period, metric, authorization, date, includeGroup);
    },
    enabled: !!centerId,
});

export const fetchSuperAdminCenterDetail = (superAdminId: string, centerId: string, period: string = 'monthly', date?: string, week?: number, day?: number, includeGroup?: boolean) => ({
    queryKey: ['superAdminCenterDetail', superAdminId, centerId, period, date || '', week ?? '', day ?? '', includeGroup ?? false],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        return await getSuperAdminCenterDetail(superAdminId, centerId, period, authorization, date, week, day, includeGroup);
    },
    enabled: !!centerId,
});

/** 근거 API 가 404 — 권한 없음·지점 없음·구 서버(라우트 없음). 다시 시도해도 같다. */
export class SuperAdminReRegistrationEvidenceUnavailableError extends Error {
    constructor() {
        super('Super admin re-registration success evidence unavailable');
        this.name = 'SuperAdminReRegistrationEvidenceUnavailableError';
    }
}

export const isSuperAdminReRegistrationEvidenceUnavailable = (error: unknown): boolean =>
    error instanceof SuperAdminReRegistrationEvidenceUnavailableError;

/**
 * 재등록 성공률 근거 모달(`Feed/ReRegistrationSuccessEvidenceModal`)에 주입하는 fetcher.
 * 모달이 넘기는 params(centerId·cohortMonth·status·page·limit·coachId)를 슈퍼관리자 API 로 보낸다.
 * search 는 서버 계약에 없어 버린다(모달도 검색창을 숨긴다).
 */
export const fetchSuperAdminReRegistrationSuccessMembers =
    (superAdminId: string) =>
    (params: {
        centerId: string;
        cohortMonth: string;
        status?: 'SUCCEEDED' | 'NOT_YET_RE_REGISTERED';
        coachId?: string;
        page?: number;
        limit?: number;
    }) => ({
        queryKey: [
            'superAdminReRegistrationSuccessMembers',
            superAdminId,
            params.centerId,
            params.coachId ?? '',
            params.cohortMonth,
            params.status ?? 'ALL',
            params.page ?? 1,
            params.limit ?? 20,
        ],
        queryFn: async () => {
            const authorization = getItemWithExpiration('authorization');
            const response = await getSuperAdminReRegistrationSuccessMembers({
                superAdminId,
                centerId: params.centerId,
                cohortMonth: params.cohortMonth,
                status: params.status,
                coachId: params.coachId,
                page: params.page,
                limit: params.limit,
                accessToken: authorization,
            });
            if (response?.status === 404) throw new SuperAdminReRegistrationEvidenceUnavailableError();
            if (response?.status !== 200) throw new Error('Failed to fetch super admin re-registration success members');
            return response;
        },
        onSuccess: ({ data: responseData, status }: any) => {
            if (status === 200) return responseData?.data;
            throw new Error('Failed to fetch super admin re-registration success members');
        },
    });
